import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { leaguesApi, LeagueType } from '../../api/leagues';
import Tooltip from '../../components/Tooltip';
import TemplateBar from '../../components/TemplateBar';
import { useTranslation } from 'react-i18next';

const schema = z.object({
  name: z.string().min(2, 'Name erforderlich'),
  level: z.string().optional(),
  description: z.string().optional(),
  registration_deadline: z.string().optional(),
  end_date: z.string().optional(),
  fee: z.string().optional(),
  teams_per_group: z.number().min(2).max(10),
  max_players: z.string().optional(),
  qualifiers_per_group: z.number().min(1).max(8),
  match_format: z.string().optional(),
  pair_type: z.string().optional(),
  doubles_per_round: z.string().optional(),
  rounds_per_encounter: z.string().optional(),
  ranking_rule: z.string().optional(),
  league_format: z.string().optional(),
  min_players_per_team: z.string().optional(),
  play_rounds: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export default function AdminLeagueNew() {
  const navigate = useNavigate();
  const [leagueType, setLeagueType] = useState<LeagueType | '' | null>(null);
  const [error, setError] = useState('');
  const { t } = useTranslation();

  const LEAGUE_TYPE_OPTIONS: { value: LeagueType | ''; label: string; description: string; color: string; image: string; gradient: string }[] = [
    {
      value: '',
      label: t('admin.klassisch'),
      description: t('admin.klassischDesc'),
      color: 'border-primary-400',
      image: '/images/einzel.png',
      gradient: 'linear-gradient(to bottom, rgba(30,64,120,0.55) 0%, rgba(10,30,70,0.80) 100%)',
    },
    {
      value: 'PAAR_LIGA',
      label: t('admin.paarLiga'),
      description: t('admin.paarLigaDesc'),
      color: 'border-green-400',
      image: '/images/paar.png',
      gradient: 'linear-gradient(to bottom, rgba(20,90,40,0.50) 0%, rgba(10,50,20,0.82) 100%)',
    },
    {
      value: 'MANNSCHAFTS_LIGA',
      label: t('admin.mannsLiga'),
      description: t('admin.mannsLigaDesc'),
      color: 'border-orange-400',
      image: '/images/mannschaft.png',
      gradient: 'linear-gradient(to bottom, rgba(120,50,10,0.50) 0%, rgba(80,25,5,0.82) 100%)',
    },
  ];

  const { register, handleSubmit, watch, reset, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      teams_per_group: 5,
      qualifiers_per_group: 2,
      match_format: 'best_of_3',
      ranking_rule: 'sets',
      league_format: 'groups_ko',
      doubles_per_round: '3',
      rounds_per_encounter: '4',
      min_players_per_team: '4',
      play_rounds: '1',
    },
  });

  async function onSubmit(data: FormData) {
    setError('');
    try {
      const payload: Record<string, unknown> = {
        name: data.name,
        level: data.level || undefined,
        description: data.description || undefined,
        registration_deadline: data.registration_deadline || undefined,
        end_date: data.end_date || undefined,
        fee: data.fee ? parseFloat(data.fee) : undefined,
        teams_per_group: data.teams_per_group,
        max_players: data.max_players ? parseInt(data.max_players) : undefined,
        qualifiers_per_group: data.qualifiers_per_group,
        match_format: data.match_format || undefined,
        league_type: leagueType || undefined,
      };

      if (leagueType === 'PAAR_LIGA') {
        payload.pair_type = data.pair_type || undefined;
      }
      if (leagueType === 'MANNSCHAFTS_LIGA') {
        payload.doubles_per_round = data.doubles_per_round ? parseInt(data.doubles_per_round) : undefined;
        payload.rounds_per_encounter = data.rounds_per_encounter ? parseInt(data.rounds_per_encounter) : undefined;
        payload.ranking_rule = data.ranking_rule || undefined;
        payload.league_format = data.league_format || 'groups_ko';
        payload.min_players_per_team = data.min_players_per_team ? parseInt(data.min_players_per_team) : undefined;
        payload.play_rounds = data.play_rounds ? parseInt(data.play_rounds) : 1;
      }

      const res = await leaguesApi.create(payload as unknown as Parameters<typeof leaguesApi.create>[0]);
      navigate(`/admin/leagues/${res.data.id}`);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail ?? t('admin.errorCreate'));
    }
  }

  // Step 1: choose league type
  if (leagueType === null) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('admin.newLeagueTitle')}</h1>
        <p className="text-gray-500 mb-8">{t('admin.chooseType')}</p>

        <div className="space-y-4">
          {LEAGUE_TYPE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setLeagueType(opt.value as LeagueType | '')}
              className={`w-full text-left border-2 rounded-xl overflow-hidden transition-all hover:shadow-xl hover:scale-[1.01] ${opt.color}`}
              style={{
                backgroundImage: `${opt.gradient}, url(${opt.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center 20%',
                minHeight: '130px',
              }}
            >
              <div className="p-5 h-full flex flex-col justify-end">
                <div className="font-bold text-xl text-white drop-shadow">{opt.label}</div>
                <div className="text-sm mt-1 text-white/80 drop-shadow">{opt.description}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-6">
          <button type="button" onClick={() => navigate('/admin')} className="btn btn-secondary">
            {t('admin.cancel')}
          </button>
        </div>
      </div>
    );
  }

  const selectedOption = LEAGUE_TYPE_OPTIONS.find((o) => o.value === leagueType)!;
  const isRoundRobin = leagueType === 'MANNSCHAFTS_LIGA' && watch('league_format') === 'round_robin';

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.newLeagueTitle')}</h1>
        <span className={`badge border ${selectedOption.color}`}>{selectedOption.label}</span>
      </div>

      <div className="card">
        <TemplateBar
          templateType="league"
          subtype={leagueType ?? ''}
          currentData={watch()}
          onApply={(data) => reset(data as unknown as FormData)}
        />
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="label">
              {t('admin.nameField')}
              <Tooltip text={t('tooltips.name')} />
            </label>
            <input type="text" {...register('name')} className={`input ${errors.name ? 'input-error' : ''}`} placeholder="Sommer Liga 2024" />
            {errors.name && <p className="error-text">{errors.name.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">
                {t('admin.levelField')}
                <Tooltip text={t('tooltips.level')} />
              </label>
              <input type="text" {...register('level')} className="input" placeholder="Offen, A, B..." />
            </div>
            <div>
              <label className="label">
                {t('admin.matchFormatField')}
                <Tooltip text={t('tooltips.matchFormat')} />
              </label>
              <select {...register('match_format')} className="input">
                <option value="best_of_3">{t('admin.bestOf3')}</option>
                <option value="best_of_1">{t('admin.bestOf1')}</option>
                <option value="pro_set">{t('admin.proSet')}</option>
              </select>
            </div>
          </div>

          {/* Paar-Liga specific */}
          {leagueType === 'PAAR_LIGA' && (
            <div>
              <label className="label">
                {t('admin.pairTypeField')}
                <Tooltip text={t('tooltips.pairType')} />
              </label>
              <select {...register('pair_type')} className="input">
                <option value="">{t('admin.pairTypeOpen')}</option>
                <option value="mixed">{t('admin.pairTypeMixed')}</option>
                <option value="male">{t('admin.pairTypeMale')}</option>
                <option value="female">{t('admin.pairTypeFemale')}</option>
              </select>
            </div>
          )}

          {/* Mannschafts-Liga specific */}
          {leagueType === 'MANNSCHAFTS_LIGA' && (
            <div>
              <label className="label">
                {t('admin.leagueFormatField')}
                <Tooltip text={t('tooltips.leagueFormat')} />
              </label>
              <select {...register('league_format')} className="input">
                <option value="groups_ko">{t('admin.leagueFormatGroupsKO')}</option>
                <option value="round_robin">{t('admin.leagueFormatRoundRobin')}</option>
              </select>
            </div>
          )}
          {leagueType === 'MANNSCHAFTS_LIGA' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  {t('admin.doublesPerRoundField')}
                  <Tooltip text={t('tooltips.doublesPerRound')} />
                </label>
                <input type="number" min="1" max="10" {...register('doubles_per_round')} className="input" />
              </div>
              <div>
                <label className="label">
                  {t('admin.roundsPerEncounterField')}
                  <Tooltip text={t('tooltips.roundsPerEncounter')} />
                </label>
                <input type="number" min="1" max="10" {...register('rounds_per_encounter')} className="input" />
              </div>
              <div>
                <label className="label">
                  {t('admin.minPlayersPerTeamField')}
                  <Tooltip text={t('tooltips.minPlayersPerTeam')} />
                </label>
                <input type="number" min="2" max="20" {...register('min_players_per_team')} className="input" />
              </div>
              <div>
                <label className="label">
                  {t('admin.rankingRuleField')}
                  <Tooltip text={t('tooltips.rankingRule')} />
                </label>
                <select {...register('ranking_rule')} className="input">
                  <option value="sets">{t('admin.rankingSets')}</option>
                  <option value="matches">{t('admin.rankingMatches')}</option>
                </select>
              </div>
              {isRoundRobin && (
                <div>
                  <label className="label">
                    {t('admin.playRoundsField')}
                    <Tooltip text={t('tooltips.playRounds')} />
                  </label>
                  <select {...register('play_rounds')} className="input">
                    <option value="1">{t('admin.playRoundsHin')}</option>
                    <option value="2">{t('admin.playRoundsHinRuck')}</option>
                  </select>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="label">
              {t('admin.descriptionField')}
              <Tooltip text={t('tooltips.description')} />
            </label>
            <textarea {...register('description')} className="input" rows={3} placeholder="" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">
                {t('admin.deadlineField')}
                <Tooltip text={t('tooltips.deadline')} />
              </label>
              <input type="datetime-local" {...register('registration_deadline')} className="input" />
            </div>
            <div>
              <label className="label">
                {t('admin.endDateField')}
                <Tooltip text={t('tooltips.endDate')} />
              </label>
              <input type="date" {...register('end_date')} className="input" />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="label">
                {t('admin.feeField')}
                <Tooltip text={t('tooltips.fee')} />
              </label>
              <input type="number" step="0.01" min="0" {...register('fee')} className="input" placeholder="0" />
            </div>
            <div>
              <label className="label">
                {leagueType === 'MANNSCHAFTS_LIGA'
                  ? t('admin.maxTeams')
                  : leagueType === 'PAAR_LIGA'
                  ? t('admin.maxPairs')
                  : t('admin.maxPlayers')}
                <Tooltip text={t('tooltips.maxPlayers')} />
              </label>
              <input type="number" min="2" {...register('max_players')} className="input" placeholder="–" />
            </div>
          </div>

          {!isRoundRobin && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">
                  {leagueType === 'MANNSCHAFTS_LIGA'
                    ? t('admin.teamsPerGroupField')
                    : leagueType === 'PAAR_LIGA'
                    ? t('admin.pairsPerGroupField')
                    : t('admin.playersPerGroupField')}
                  <Tooltip text={t('tooltips.teamsPerGroup')} />
                </label>
                <input type="number" min="2" max="10" {...register('teams_per_group', { valueAsNumber: true })} className={`input ${errors.teams_per_group ? 'input-error' : ''}`} />
                {errors.teams_per_group && <p className="error-text">{errors.teams_per_group.message}</p>}
              </div>
              <div>
                <label className="label">
                  {t('admin.qualifiersPerGroupField')}
                  <Tooltip text={t('tooltips.qualifiersPerGroup')} />
                </label>
                <input type="number" min="1" max="8" {...register('qualifiers_per_group', { valueAsNumber: true })} className={`input ${errors.qualifiers_per_group ? 'input-error' : ''}`} />
                {errors.qualifiers_per_group && <p className="error-text">{errors.qualifiers_per_group.message}</p>}
              </div>
            </div>
          )}

          <div className="flex justify-between gap-3 pt-2">
            <button type="button" onClick={() => setLeagueType(null)} className="btn btn-secondary">
              {t('admin.changeType')}
            </button>
            <div className="flex gap-3">
              <button type="button" onClick={() => navigate('/admin')} className="btn btn-secondary">
                {t('admin.cancel')}
              </button>
              <button type="submit" disabled={isSubmitting} className="btn btn-primary">
                {isSubmitting ? t('admin.creating') : t('admin.createLeagueButton')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
